import { action, computed, observable } from 'mobx'
import { config } from '@config'
import LocalizationService from '../services/LocalizationService'
import filter from 'lodash/filter'
import { DefaultConfig } from '@constants'

interface ITranslation {
  language: string
  content: any
}
export class LocalizationStore {
  localizationService = new LocalizationService()

  @observable languages = [
    {
      key: 'ar-XA',
      name: 'Arabic',
      displayName: 'Arabic (العربية)'
    },
    {
      key: 'bg',
      name: 'Bulgarian',
      displayName: 'Bulgarian (Български)'
    },
    {
      key: 'zh-chs',
      name: 'Chinese (Simplified)',
      displayName: 'Chinese (Simplified) (简体中文)'
    },
    {
      key: 'zh-cht',
      name: 'Chinese (Traditional)',
      displayName: 'Chinese (Traditional) (中國傳統的'
    },
    {
      key: 'hr',
      name: 'Croatian',
      displayName: 'Croatian (Hrvatski)'
    },
    {
      key: 'cs',
      name: 'Czech',
      displayName: 'Czech (Čeština)'
    },
    {
      key: 'da',
      name: 'Danish',
      displayName: 'Danish (Dansk)'
    },
    {
      key: 'nl',
      name: 'Dutch',
      displayName: 'Dutch (Nederlands)'
    },
    {
      key: 'en',
      name: 'English',
      displayName: 'English (English)'
    },
    {
      key: 'et',
      name: 'Estonian',
      displayName: 'Estonian (Eesti)'
    },
    {
      key: 'fi',
      name: 'Finnish',
      displayName: 'Finnish (Suomi)'
    },
    {
      key: 'fr',
      name: 'French',
      displayName: 'French (Français)'
    },
    {
      key: 'de',
      name: 'German',
      displayName: 'German (Deutsch)'
    },
    {
      key: 'el',
      name: 'Greek',
      displayName: 'Greek (Ελληνικά)'
    },
    {
      key: 'he',
      name: 'Hebrew',
      displayName: 'Hebrew (עברית)'
    },
    {
      key: 'hi',
      name: 'Hindi',
      displayName: 'Hindi (हिन्दी)'
    },
    {
      key: 'hu',
      name: 'Hungarian',
      displayName: 'Hungarian (Magyar)'
    },
    {
      key: 'ga',
      name: 'Irish',
      displayName: 'Irish (Gaeilge)'
    },
    {
      key: 'it',
      name: 'Italian',
      displayName: 'Italian (Italiano)'
    },
    {
      key: 'ja',
      name: 'Japanese',
      displayName: 'Japanese (日本語)'
    },
    {
      key: 'ko',
      name: 'Korean',
      displayName: 'Korean (한국어)'
    },
    {
      key: 'lv',
      name: 'Latvian',
      displayName: 'Latvian (Latviešu)'
    },
    {
      key: 'lt',
      name: 'Lithuanian',
      displayName: 'Lithuanian (Lietuvių)'
    },
    {
      key: 'no',
      name: 'Norwegian',
      displayName: 'Norwegian (Norsk)'
    },
    {
      key: 'pl',
      name: 'Polish',
      displayName: 'Polish (Polski)'
    },
    {
      key: 'pt',
      name: 'Portuguese',
      displayName: 'Portuguese (Português)'
    },
    {
      key: 'ro',
      name: 'Romanian',
      displayName: 'Romanian (Română)'
    },
    {
      key: 'ru',
      name: 'Russian',
      displayName: 'Russian (Русский)'
    },
    {
      key: 'sr-CS',
      name: 'Serbian',
      displayName: 'Serbian (Српски)'
    },
    {
      key: 'sk',
      name: 'Slovak',
      displayName: 'Slovak (Slovenčina)'
    },
    {
      key: 'sl',
      name: 'Slovenian',
      displayName: 'Slovenian (Slovenščina)'
    },
    {
      key: 'es',
      name: 'Spanish',
      displayName: 'Spanish (Español)'
    },
    {
      key: 'sv',
      name: 'Swedish',
      displayName: 'Swedish (Svenska)'
    },
    {
      key: 'th',
      name: 'Thai',
      displayName: 'Thai (ไทย)'
    },
    {
      key: 'tr',
      name: 'Turkish',
      displayName: 'Turkish (Türkçe)'
    },
    {
      key: 'uk-UA',
      name: 'Ukrainian',
      displayName: 'Ukrainian (Українська)'
    }
  ]
  @observable supportedLanguageCodes = ['en']

  @observable languageBundle = {}

  // return languages which are in supported language codes. The order of languages should be same as the order in supported language codes
  @computed get supportedLanguages() {
    const supportedLanguageCodes = this.supportedLanguageCodes
    const supportedLanguages = this.languages.filter(language =>
      supportedLanguageCodes.includes(language.key)
    )
    supportedLanguages.sort(function (a, b) {
      return supportedLanguageCodes.indexOf(a.key) - supportedLanguageCodes.indexOf(b.key)
    })
    return supportedLanguages
  }

  @action
  getLanguages = async () => {
    this.languages = await this.localizationService.getLanguages()
    return this.languages
  }

  @action
  getSupportedLanguages = async () => {
    let supportedLanguages = await this.localizationService.getSupportedLanguages()
    // GBB-10464: Setting default Langauge en to first position in supportedLanguages list.
    const defaultLanguage =
      config.localization.defaultLanguage || DefaultConfig.LOCALIZATION_DEFAULT_LANGUAGE
    supportedLanguages = filter(supportedLanguages, lang => lang !== defaultLanguage)
    supportedLanguages.unshift(defaultLanguage)
    this.supportedLanguageCodes = supportedLanguages
    return this.supportedLanguages
  }

  @action
  addSupportedLanguage = async code => {
    await this.localizationService.addSupportedLanguage(code)
  }

  @action
  deleteSupportedLanguage = async code => {
    await this.localizationService.deleteSupportedLanguage(code)
  }

  @action
  getLanguageBundle = async () => {
    this.languageBundle = await this.localizationService.getLanguageBundle()
    return this.languageBundle
  }

  @action
  updateLanguageBundle = async (rows: any[]) => {
    if (rows.length > 0) {
      const supportedLanguages = await this.getSupportedLanguages()
      const uploadBundle = {}
      for (const language of supportedLanguages) {
        const sourceTranslationBundle = this.localizationService.getLanguageTranslations(
          language.key
        )
        const updatedTranslations = {}
        for (const row of rows) {
          const translationKey = row['key']
          // In case current translations for the translation key is not available
          const sourceTranslation = sourceTranslationBundle[translationKey] || ''
          const updatedTranslation = row[language.displayName]
          if (
            updatedTranslation &&
            sourceTranslation?.trim() !== updatedTranslation.trim()
          ) {
            updatedTranslations[translationKey] = updatedTranslation
          }
        }
        uploadBundle[language.key] = updatedTranslations
      }
      await this.localizationService.updateLanguageBundle(uploadBundle)
    }
  }

  @computed get csv() {
    const header = [
      'key',
      ...this.supportedLanguages.map(language => language.displayName)
    ]
    const listOfTranslations = this.supportedLanguages.map(language => {
      const mergedTranslations = this.localizationService.getLanguageTranslations(
        language.key
      )
      return { language: language.key, content: mergedTranslations } as ITranslation
    })
    const translations = {}
    for (const translation of listOfTranslations) {
      translations[translation['language']] = translation['content']
    }
    const rows = []
    const defaultLanguage =
      config.localization.defaultLanguage || DefaultConfig.LOCALIZATION_DEFAULT_LANGUAGE
    const otherLanguages = this.supportedLanguages.filter(x => x.key !== 'en')
    const refTranlation = translations[defaultLanguage]
    //FIXME - Logic is broken when we have more languages than default language
    for (const translationKey of Object.keys(refTranlation)) {
      const row = []
      row.push(translationKey)
      row.push(refTranlation[translationKey])
      for (const otherLanguage of otherLanguages) {
        row.push(translations[otherLanguage.key][translationKey])
      }
      rows.push(row)
    }
    const result = [header, ...rows]
    return result
  }
}
const store = new LocalizationStore()
export default store
